import * as React from 'react';

import {
    Line,
    LineChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Rectangle,
    // @ts-expect-error
} from 'recharts';

import {colors} from 'shells/colors/colors';
import {StarIcon} from 'shells/icon';
import {RANKED_COLORS} from 'shells/maps/consts';
import type {Item} from 'shells/table/item-count-table';

import {TrafficLineChartTooltip} from '.';

type Props = {
    domains: Item[];
    website: string | null | undefined;
};

export function TrafficOverTimeLineChart(props: Props) {
    const months =
        props.domains[0] && props.domains[0].segments
            ? props.domains[0].segments.map((seg) => seg.bucket)
            : [];

    const domains = props.domains.map((domain, index) => ({
        name: domain.bucket,
        // @ts-expect-error
        fill: colors[RANKED_COLORS[index]],
    }));

    const data = months.map((month) => {
        const monthData = {
            monthName: month,
        } as const;

        props.domains.forEach((domain) => {
            const segment = domain.segments && domain.segments.find((seg) => seg.bucket === month);

            // @ts-expect-error
            monthData[domain.bucket] = segment ? segment.count.value : 0;
        });

        return monthData;
    });

    const renderTooltip = (tooltipProps: {
        active: boolean;
        payload: {
            payload: any[];
        };
    }) => {
        return <TrafficLineChartTooltip {...tooltipProps} domains={domains} />;
    };

    return (
        <ResponsiveContainer height={400} width='100%'>
            <LineChart
                width={1000}
                height={360}
                data={data}
                margin={{
                    top: 40,
                    right: 80,
                    left: 60,
                    bottom: 20,
                }}
            >
                <Legend
                    verticalAlign='top'
                    align='right'
                    wrapperStyle={{top: '20px'}}
                    iconSize={10}
                    iconType='circle'
                    formatter={(value: string) => {
                        if (value === props.website) {
                            return (
                                <span>
                                    <span className='mr-4'>{value}</span>
                                    <span>
                                        (<StarIcon size={12} />)
                                    </span>
                                </span>
                            );
                        } else {
                            return value;
                        }
                    }}
                />
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='monthName' />
                <YAxis type='number' tickFormatter={(count: number) => count.toLocaleString()} />
                <Tooltip content={renderTooltip} cursor={<CustomCursor months={months} />} />
                {domains.map((domain) => (
                    <Line
                        className='pointer-cursor'
                        key={domain.name}
                        dataKey={domain.name}
                        type='monotone'
                        stroke={domain.fill}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

const CustomCursor = (props: any) => {
    const {points, left, height, months, payload} = props;

    let x;
    if (payload[0].payload.monthName === months[0]) {
        x = points[0].x + 1; // allows y axis to show
    } else {
        x = points[0].x - left / 2;
    }

    let width;
    if (
        payload[0].payload.monthName === months[0] ||
        payload[0].payload.monthName === months[months.length - 1]
    ) {
        width = left / 2;
    } else {
        width = left;
    }

    return (
        <Rectangle
            fill={colors.offWhiteMd}
            stroke={colors.offWhiteMd}
            x={x}
            y={points[0].y}
            width={width}
            height={height - 1} // allows x axis to show
        />
    );
};
